import React, { useState, useRef, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Send, Paperclip, X, Image, Video, Plus, Smile, Zap } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import EmojiPicker from "./EmojiPicker";
import GifPicker from "./GifPicker";
import { useTypingIndicator } from "./TypingIndicator";
import ReplyPreview from "./ReplyPreview";
import EmojiSticker from "./EmojiSticker";

interface MediaPreview {
  url: string;
  type: 'image' | 'video';
  filename: string;
  size: number;
  file: File;
  isLoading?: boolean;
}

interface DiscordLikeInputProps {
  onSendMessage: (message: string, mediaData?: {
    mediaUrl: string;
    mediaType: 'image' | 'video' | 'gif';
    mediaFilename: string;
    mediaSize: number;
  }, replyToId?: string) => Promise<void>;
  disabled?: boolean;
  placeholder?: string;
  roomId?: string;
  replyTo?: {
    id: string;
    content: string;
    sender_id: string;
    media_type?: string;
    media_url?: string;
  } | null;
  onClearReply?: () => void;
  senderName?: string;
}

export default function DiscordLikeInput({ 
  onSendMessage, 
  disabled = false, 
  placeholder = "Message...",
  roomId,
  replyTo,
  onClearReply,
  senderName
}: DiscordLikeInputProps) {
  const [message, setMessage] = useState("");
  const [mediaPreview, setMediaPreview] = useState<MediaPreview | null>(null);
  const [isSending, setIsSending] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [isDragOver, setIsDragOver] = useState(false);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [showGifPicker, setShowGifPicker] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { user } = useAuth();
  const { setTyping } = useTypingIndicator(roomId || '');
  
  let typingTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Handle typing indicators
  const handleTyping = () => {
    if (!roomId) return;
    
    setTyping(true);
    
    // Clear existing timeout
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }
    
    // Set new timeout to stop typing after 3 seconds
    typingTimeoutRef.current = setTimeout(() => {
      setTyping(false);
    }, 3000);
  };

  const stopTyping = () => {
    if (!roomId) return;
    
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
      typingTimeoutRef.current = null;
    }
    setTyping(false);
  };

  // Auto-resize textarea like Discord
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      const newHeight = Math.min(textareaRef.current.scrollHeight, 200);
      textareaRef.current.style.height = newHeight + 'px';
    }
  }, [message]);

  const handleGifSelect = async (gifUrl: string) => {
    console.log('🎬 GIF Selected:', gifUrl);
    setShowGifPicker(false);
    
    // Send GIF as a message
    try {
      setIsSending(true);
      console.log('📤 Sending GIF message...');
      
      await onSendMessage("", {
        mediaUrl: gifUrl,
        mediaType: 'gif',
        mediaFilename: 'animated.gif',
        mediaSize: 0
      }, replyTo?.id);
      
      console.log('✅ GIF sent successfully');
      
      // Clear reply after sending
      if (replyTo && onClearReply) {
        onClearReply();
      }
      stopTyping();
    } catch (error) {
      console.error("❌ Failed to send GIF:", error);
      toast.error("Failed to send GIF: " + (error as Error).message);
    } finally {
      setIsSending(false);
    }
  };

  const uploadMediaToStorage = async (file: File): Promise<string | null> => {
    if (!user) return null;

    try {
      const fileExt = file.name.split('.').pop();
      const fileName = `${user.id}/${Date.now()}.${fileExt}`;
      
      const { data, error } = await supabase.storage
        .from('chat-media')
        .upload(fileName, file);

      if (error) {
        console.error('Upload error:', error);
        toast.error("Failed to upload file: " + error.message);
        return null;
      }

      const { data: { publicUrl } } = supabase.storage
        .from('chat-media')
        .getPublicUrl(data.path);

      return publicUrl;
    } catch (error) {
      console.error('Upload error:', error);
      toast.error("Failed to upload file");
      return null;
    }
  };

  const handleSubmit = async (e?: React.FormEvent) => {
    e?.preventDefault();
    
    if ((!message.trim() && !mediaPreview) || isSending || disabled || !user) return;

    setIsSending(true);
    setIsUploading(true);
    try {
      let mediaData;
      
      if (mediaPreview) {
        // Show uploading state
        setMediaPreview(prev => prev ? { ...prev, isLoading: true } : null);
        
        const uploadedUrl = await uploadMediaToStorage(mediaPreview.file);
        if (!uploadedUrl) {
          toast.error("Failed to upload media");
          setMediaPreview(prev => prev ? { ...prev, isLoading: false } : null);
          return;
        }
        
        mediaData = {
          mediaUrl: uploadedUrl,
          mediaType: mediaPreview.type,
          mediaFilename: mediaPreview.filename,
          mediaSize: mediaPreview.size
        };
        
        // Clean up preview
        URL.revokeObjectURL(mediaPreview.url);
        setMediaPreview(null);
      }

      await onSendMessage(message.trim(), mediaData, replyTo?.id);
      setMessage("");
      
      // Clear reply after sending
      if (replyTo && onClearReply) {
        onClearReply();
      }
      
      stopTyping();
      
      // Focus back to input
      textareaRef.current?.focus();
      
    } catch (error) {
      console.error("Failed to send message:", error);
      toast.error("Failed to send message");
      // Reset loading state on error
      setMediaPreview(prev => prev ? { ...prev, isLoading: false } : null);
    } finally {
      setIsSending(false);
      setIsUploading(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
      stopTyping();
    } else {
      handleTyping();
    }
  };

  const handleFileSelect = (files: FileList | File[]) => {
    const file = files[0];
    if (!file) return;

    // Check file size (50MB limit)
    if (file.size > 52428800) {
      toast.error("File size must be less than 50MB");
      return;
    }

    // Check file type
    const allowedTypes = [
      'image/jpeg', 'image/png', 'image/gif', 'image/webp',
      'video/mp4', 'video/webm', 'video/quicktime'
    ];
    
    if (!allowedTypes.includes(file.type)) {
      toast.error("Unsupported file type. Please use JPEG, PNG, GIF, WebP for images or MP4, WebM for videos.");
      return;
    }

    const mediaType = file.type.startsWith('image/') ? 'image' : 'video';
    
    // Show immediate loading state
    const preview: MediaPreview = {
      url: URL.createObjectURL(file),
      type: mediaType,
      filename: file.name,
      size: file.size,
      file,
      isLoading: true
    };
    
    setMediaPreview(preview);
    
    // Clear the file input immediately to dismiss picker
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    
    // After a short delay, remove loading state to show the actual preview
    setTimeout(() => {
      setMediaPreview(prev => prev ? { ...prev, isLoading: false } : null);
    }, 500);
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      handleFileSelect(e.target.files);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
    
    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      handleFileSelect(files);
    }
  };

  const removeMediaPreview = () => {
    if (mediaPreview) {
      URL.revokeObjectURL(mediaPreview.url);
      setMediaPreview(null);
    }
  };

  const handleEmojiSelect = (emoji: string) => {
    setMessage(prev => prev + emoji);
    setShowEmojiPicker(false);
    textareaRef.current?.focus();
  };

  const handleStickerSelect = (emoji: string) => {
    // For stickers, we'll send them as a message directly
    const stickerMessage = `${emoji}`;
    setMessage(stickerMessage);
    setShowEmojiPicker(false);
    // Auto-send sticker
    setTimeout(() => {
      if (stickerMessage.trim()) {
        handleSubmit();
      }
    }, 100);
  };

  return (
    <div className="relative">
      {/* Reply Preview */}
      {replyTo && (
        <ReplyPreview
          originalMessage={replyTo}
          senderName={senderName || 'Unknown User'}
          onClear={onClearReply || (() => {})}
        />
      )}

      {/* Media Preview */}
      {mediaPreview && (
        <div className="mx-4 mb-2 p-3 bg-muted/50 rounded-t-xl border border-b-0 border-border">
          <div className="flex items-start gap-3">
            <div className="relative flex-shrink-0">
              {mediaPreview.isLoading ? (
                <div className="w-16 h-16 bg-muted rounded-lg flex items-center justify-center">
                  <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                </div>
              ) : mediaPreview.type === 'image' ? (
                <img
                  src={mediaPreview.url}
                  alt={mediaPreview.filename}
                  className="w-16 h-16 object-cover rounded-lg"
                />
              ) : (
                <div className="relative w-16 h-16 bg-muted rounded-lg flex items-center justify-center">
                  <Video className="w-6 h-6 text-muted-foreground" />
                  <video
                    src={mediaPreview.url}
                    className="absolute inset-0 w-full h-full object-cover rounded-lg opacity-50"
                  />
                </div>
              )}
              
              {(mediaPreview.isLoading || isUploading) && (
                <div className="absolute inset-0 bg-black/20 rounded-lg flex items-center justify-center">
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                </div>
              )}
              
              <Button
                size="sm"
                variant="secondary"
                onClick={removeMediaPreview}
                disabled={mediaPreview.isLoading || isUploading}
                className="absolute -top-1 -right-1 h-5 w-5 p-0 rounded-full"
              >
                <X className="h-3 w-3" />
              </Button>
            </div>
            
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 text-sm font-medium">
                {mediaPreview.type === 'image' ? (
                  <Image className="h-4 w-4 text-blue-500" />
                ) : (
                  <Video className="h-4 w-4 text-purple-500" />
                )}
                <span className="truncate">{mediaPreview.filename}</span>
                {(mediaPreview.isLoading || isUploading) && (
                  <span className="text-xs text-muted-foreground ml-2">
                    {isUploading ? "Uploading..." : "Processing..."}
                  </span>
                )}
              </div>
              <p className="text-xs text-muted-foreground">
                {(mediaPreview.size / 1024 / 1024).toFixed(1)}MB
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Input Area */}
      <div 
        className={`mx-4 mb-4 relative ${isDragOver ? 'ring-2 ring-primary ring-offset-2' : ''}`}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        <div className={`bg-background border border-border rounded-xl ${mediaPreview ? 'rounded-t-none' : ''} shadow-sm transition-all duration-200 ${isDragOver ? 'border-primary bg-primary/5' : ''}`}>
          <div className="flex items-end gap-2 p-3">
            {/* Plus/Attach Button */}
            <div className="relative">
              <Button
                type="button"
                size="sm"
                variant="ghost"
                onClick={() => fileInputRef.current?.click()}
                disabled={disabled || isSending}
                className="h-8 w-8 p-0 text-muted-foreground hover:text-primary"
              >
                <Plus className="h-4 w-4" />
              </Button>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*,video/*"
                onChange={handleFileInputChange}
                className="hidden"
                disabled={disabled || isSending}
              />
            </div>

            {/* Text Input */}
            <div className="flex-1 relative">
              <textarea
                ref={textareaRef}
                value={message}
                onChange={(e) => {
                  setMessage(e.target.value);
                  handleTyping();
                }}
                onKeyDown={handleKeyPress}
                onBlur={stopTyping}
                placeholder={mediaPreview ? "Add a message..." : placeholder}
                disabled={disabled || isSending}
                className="w-full bg-transparent border-0 outline-none resize-none text-sm placeholder:text-muted-foreground min-h-[20px] max-h-[200px] leading-5"
                rows={1}
                maxLength={2000}
              />
            </div>

            {/* GIF Button */}
            <div className="relative">
              <Button
                type="button"
                size="sm"
                variant="ghost"
                onClick={() => {
                  setShowGifPicker(!showGifPicker);
                  setShowEmojiPicker(false);
                }}
                disabled={disabled || isSending}
                className="h-8 w-8 p-0 text-muted-foreground hover:text-primary"
              >
                <Zap className="h-4 w-4" />
              </Button>
              
              {showGifPicker && (
                <>
                  <div 
                    className="fixed inset-0 z-40" 
                    onClick={() => setShowGifPicker(false)}
                  />
                  <GifPicker 
                    onGifSelect={handleGifSelect}
                    onClose={() => setShowGifPicker(false)}
                  />
                </>
              )}
            </div>

            {/* Single Emoji Picker Button */}
            <div className="relative">
              <Button
                type="button"
                size="sm"
                variant="ghost"
                onClick={() => {
                  setShowEmojiPicker(!showEmojiPicker);
                  setShowGifPicker(false);
                }}
                disabled={disabled || isSending}
                className="h-8 w-8 p-0 text-muted-foreground hover:text-primary"
              >
                <Smile className="h-4 w-4" />
              </Button>
               {showEmojiPicker && (
                 <>
                   <div 
                     className="fixed inset-0 z-40" 
                     onClick={() => setShowEmojiPicker(false)}
                   />
                   <div className="absolute bottom-full right-0 mb-2 z-50">
                     <EmojiPicker 
                       onEmojiSelect={handleEmojiSelect}
                       disabled={disabled || isSending}
                       noTrigger={true}
                     />
                   </div>
                 </>
               )}
            </div>

            {/* Send Button */}
            <Button 
              onClick={handleSubmit}
              disabled={(!message.trim() && !mediaPreview) || disabled || isSending || isUploading || mediaPreview?.isLoading}
              size="sm"
              className="h-8 w-8 p-0 bg-primary hover:bg-primary/90 text-primary-foreground rounded-lg transition-all"
            >
              {(isSending || isUploading) ? (
                <div className="w-4 h-4 border-2 border-primary-foreground border-t-transparent rounded-full animate-spin" />
              ) : (
                <Send className="h-4 w-4" />
              )}
            </Button>
          </div>
        </div>

        {/* Drag Overlay */}
        {isDragOver && (
          <div className="absolute inset-0 bg-primary/10 border-2 border-dashed border-primary rounded-xl flex items-center justify-center">
            <div className="text-center">
              <Paperclip className="h-8 w-8 mx-auto mb-2 text-primary" />
              <p className="text-sm font-medium text-primary">Drop your files here</p>
            </div>
          </div>
        )}
      </div>

      {/* Click outside to close pickers */}
      {(showEmojiPicker || showGifPicker) && (
        <div 
          className="fixed inset-0 z-40" 
          onClick={() => {
            setShowEmojiPicker(false);
            setShowGifPicker(false);
          }}
        />
      )}
    </div>
  );
}